import { useContext, useState } from 'react'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { axios } from 'api'
import { calculatePriceSummary } from 'utils/feesCalculation'
import { Item } from 'hooks/useProduct'
import { downloadPdfFromBase64 } from 'utils/downloadPdfFromBase64'
import { useTranslation } from 'react-i18next'

export type Cart = {
  id: string
  poNumber?: string
  items: Item[]
  isTaxable?: boolean
}

export type DiscountResult = {
  isValid: boolean
  message: string
  rate?: number
  appliedDiscountId?: string
}

export type BatchColorFailedItem = {
  cartItemId: string
  sku: string
}

const DISCOUNT_CODE_KEY = 'cart_discount_code'
const DISCOUNT_RESULT_KEY = 'cart_discount_result'

const useCart = () => {
  const { t } = useTranslation()
  const [cart, setCart] = useState<Cart>({
    id: '',
    items: []
  })

  const { showMessage } = useContext(SnackbarContext)
  const [quotes, setQuotes] = useState<Cart[]>([])
  const [discountCode, setDiscountCodeState] = useState<string>(() => {
    const saved = localStorage.getItem(DISCOUNT_CODE_KEY)
    return saved || ''
  })
  const [discountResult, setDiscountResultState] =
    useState<DiscountResult | null>(() => {
      const saved = localStorage.getItem(DISCOUNT_RESULT_KEY)
      return saved ? JSON.parse(saved) : null
    })
  const [isCartReady, setIsCartReady] = useState(false)

  const setDiscountCode = (value: ((prevState: string) => string) | string) => {
    setDiscountCodeState(prevCode => {
      const code = typeof value === 'function' ? value(prevCode) : value
      if (code) {
        localStorage.setItem(DISCOUNT_CODE_KEY, code)
      } else {
        localStorage.removeItem(DISCOUNT_CODE_KEY)
      }
      return code
    })
  }

  const setDiscountResult = (result: DiscountResult | null) => {
    setDiscountResultState(result)
    if (result) {
      localStorage.setItem(DISCOUNT_RESULT_KEY, JSON.stringify(result))
    } else {
      localStorage.removeItem(DISCOUNT_RESULT_KEY)
    }
  }

  const fetchCart = async () => {
    const response = await axios.get('/carts')
    if (response.status === 200) {
      setCart(response.data)
      setIsCartReady(true)
    } else {
      console.error('Failed to load cart items.')
    }
  }

  const updateCartItem = async (updateData: {
    cartItemId?: string
    productId?: string
    quantity?: number
    isAssembled?: boolean
    hingeDirection?: string | null
    skuPrefix?: string
    poNumber?: string
  }) => {
    const response = await axios.post('/carts', updateData)
    if (response.status === 200) {
      setCart(response.data)
      showMessage(t('snackbar.cart.itemUpdated'))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const deleteCartItem = async (cartItemId: string) => {
    const response = await axios.post('/carts', {
      cartItemId,
      quantity: 0
    })
    if (response.status === 200) {
      setCart(response.data)
      showMessage(t('snackbar.cart.itemDeleted'))
    } else {
      showMessage(t('snackbar.cart.deleteFailed'), 'error')
    }
  }

  const clearCart = async () => {
    const response = await axios.delete('/carts')
    if (response.status === 204) {
      clearCartUi()
      setDiscountCode('')
      setDiscountResult(null)
    } else {
      showMessage(t('snackbar.cart.clearFailed'), 'error')
    }
  }

  const clearCartUi = () => {
    setCart({
      id: '',
      poNumber: '',
      items: []
    })
    setDiscountCode('')
    setDiscountResult(null)
  }

  const clearQuotesUi = () => {
    setQuotes(prevQuotes =>
      prevQuotes.map(quote => ({
        ...quote,
        items: [],
        poNumber: '',
        isTaxable: false
      }))
    )
  }

  const switchToQuote = async () => {
    const response = await axios.post('/carts/switch-to-quote')
    if (response.status === 200) {
      const newQuote = response.data
      setQuotes(prevQuotes => [...prevQuotes, newQuote])
      setDiscountCode('')
      setDiscountResult(null)
      showMessage(t('snackbar.quote.switchedToCart'))
    } else {
      console.error('Failed to create a quote.')
    }
  }

  const fetchQuotes = async () => {
    const response = await axios.get('/carts/all-quotes')
    if (response.status === 200) {
      setQuotes(response.data)
    } else {
      console.error('Failed to fetch all Quotes')
      setQuotes([])
    }
  }

  const updateQuoteItem = async (updateData: {
    quoteId: string
    cartItemId?: string
    quantity?: number
    isAssembled?: boolean
    hingeDirection?: string
    skuPrefix?: string
    poNumber?: string
  }) => {
    const {
      quoteId,
      cartItemId,
      quantity,
      isAssembled,
      hingeDirection,
      skuPrefix,
      poNumber
    } = updateData
    const response = await axios.post('/carts', {
      quoteId,
      cartItemId,
      quantity,
      isAssembled,
      hingeDirection,
      skuPrefix,
      poNumber
    })
    const updatedQuote = response.data
    if (response.status === 200) {
      setQuotes(prevQuotes => {
        if (
          !updatedQuote ||
          !updatedQuote.items ||
          updatedQuote.items.length === 0
        ) {
          return prevQuotes.filter(quote => quote.id !== quoteId)
        } else {
          return prevQuotes.map(quote =>
            quote.id === quoteId ? updatedQuote : quote
          )
        }
      })
      showMessage(t('snackbar.quote.itemUpdated'))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const batchUpdateColor = async (
    skuPrefix: string,
    items: Item[],
    cartId: string,
    isQuote = false
  ): Promise<{
    notFound: BatchColorFailedItem[]
    outOfStock: BatchColorFailedItem[]
  } | null> => {
    if (items.length === 0) {
      return { notFound: [], outOfStock: [] }
    }

    const response = await axios.patch(`/carts/${cartId}/batch-color`, {
      cartItemIds: items.map(item => item.cartItemId),
      skuPrefix
    })

    if (response.status !== 200) {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
      return null
    }

    const { cart: updated, notFound = [], outOfStock = [] } =
      response.data

    if (updated && updated.items) {
      if (isQuote) {
        setQuotes(prevQuotes =>
          prevQuotes.map(quote => (quote.id === cartId ? updated : quote))
        )
      } else {
        setCart(updated)
      }
    }

    return {
      notFound,
      outOfStock
    }
  }

  const deleteQuoteItem = async (quoteId: string, cartItemId: string) => {
    await updateQuoteItem({ quoteId, cartItemId, quantity: 0 })
  }

  const clearQuote = async (quoteId: string) => {
    const response = await axios.delete(`/carts/${quoteId}`)
    if (response.status === 204) {
      setQuotes(prevQuotes => prevQuotes.filter(quote => quote.id !== quoteId))
    } else {
      showMessage(t('snackbar.quote.clearFailed'), 'error')
    }
  }

  const downloadQuotePdf = async (quoteId: string) => {
    const response = await axios.get(`/carts/${quoteId}/pdf`)
    const pdfBase64 = response.data.pdf
    const fileName = response.data.fileName
    if (pdfBase64) {
      downloadPdfFromBase64(pdfBase64, fileName)
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const switchToCart = async (quoteId: string) => {
    const previousActiveCart = { ...cart }

    const response = await axios.put(`/carts/switch-to-cart/${quoteId}`)
    if (response.status === 200) {
      setCart(response.data)
      setDiscountCode('')
      setDiscountResult(null)

      setQuotes(prevQuotes => {
        const updatedQuotes = prevQuotes.filter(quote => quote.id !== quoteId)

        // The backend turns the outgoing cart into a quote, except when it
        // is empty -- it discards that one, so do not list it here either.
        if (previousActiveCart.id && previousActiveCart.items?.length) {
          updatedQuotes.push({
            id: previousActiveCart.id,
            poNumber: previousActiveCart.poNumber,
            items: previousActiveCart.items
          })
        }

        return updatedQuotes
      })

      showMessage(t('snackbar.quote.switchedToCart'))
    } else if (response.status === 409) {
      // Another tab or device already moved this account's cart on. Local
      // state is describing something that no longer exists, so reload it
      // instead of leaving the stale cart on screen.
      await Promise.all([fetchCart(), fetchQuotes()])
      showMessage(
        response.data?.error || t('snackbar.quote.switchFailed'),
        'error'
      )
    } else {
      showMessage(t('snackbar.quote.switchFailed'), 'error')
    }
  }

  const validateDiscountCode = async (code: string) => {
    const response = await axios.post('/coupons/validate', { code })
    if (response.data.isValid) {
      const allowedProductLines: string[] =
        response.data.allowedProductLines || []
      if (allowedProductLines.length > 0) {
        const hasMatchingItem = cart.items.some(item =>
          allowedProductLines.some(prefix =>
            item.sku.toUpperCase().startsWith(prefix.toUpperCase())
          )
        )
        if (!hasMatchingItem) {
          setDiscountResult(null)
          setDiscountCode('')
          showMessage(
            t('discountCode.productLineOnly', {
              productLines: allowedProductLines.join(', ')
            }),
            'error'
          )
          return
        }
      }
      setDiscountCode(code)
      setDiscountResult(response.data)
      showMessage(response.data.message)
    } else {
      setDiscountResult(null)
      setDiscountCode('')
      showMessage(response.data.message, 'error')
    }
  }

  const removeDiscount = () => {
    setDiscountCode('')
    setDiscountResult(null)
    showMessage(t('snackbar.cart.discountRemoved'))
  }

  const getCartDetails = () => {
    const discountRate = discountResult?.rate ?? 0
    const discountValid = discountResult?.isValid ?? false

    const summary = calculatePriceSummary(cart, discountRate, discountValid)

    return {
      cartItemCount:
        cart?.items?.reduce((total, item) => total + item.quantity, 0) || 0,
      ...summary
    }
  }

  const getQuoteDetails = (quote: Cart) => {
    return calculatePriceSummary(quote)
  }

  return {
    cart,
    setCart,
    fetchCart,
    updateCartItem,
    deleteCartItem,
    clearCart,
    clearCartUi,
    clearQuotesUi,
    quotes,
    setQuotes,
    switchToQuote,
    fetchQuotes,
    updateQuoteItem,
    deleteQuoteItem,
    batchUpdateColor,
    clearQuote,
    downloadQuotePdf,
    switchToCart,
    validateDiscountCode,
    removeDiscount,
    discountCode,
    setDiscountCode,
    discountResult,
    getCartDetails,
    getQuoteDetails,
    isCartReady
  }
}

export default useCart
